/**
 * Which of the certificate actions a SELECTION supports.
 *
 * Bulk actions are selection-dependent: offering an action for a set that
 * contains a stolen item, or one already mid-transfer, promises something the
 * backend will refuse. The failure then arrives after the user has committed,
 * which is the worst place to learn it.
 *
 * ## Why this is no longer named after "transfer"
 *
 * It was `getTransferAvailability`, sized to the one action the bulk bar used to
 * offer — an action this app does not actually have (the bar's handler was a
 * `TODO` that silently did nothing). The bar now offers the SAME set a single
 * certificate does (see `certificateActionItems`), so the helper is per-action
 * and the name says which action it is answering for.
 */

interface CertificateLike {
  id?: string;
  status?: string | null;
  product?: { title?: string | null } | null;
  name?: string | null;
}

/**
 * Idle and owned — the one state from which a discretionary action can start.
 *
 * - Already mid-transfer — a second action would race the first.
 * - `lost` / `stolen` — you do not have the item to hand over, and acting on it
 *   would launder the very state the flag exists to record.
 * - `inactive` / `returned` — not in the owner's possession as a live certificate.
 *
 * Named to match `isIdleOwned` on the certificate page, which gates the same set
 * of actions for a single record with the same predicate. The two ARE the same
 * question asked of one record and of N.
 */
export const isIdleOwned = (certificate?: CertificateLike | null): boolean =>
  certificate?.status === 'active';

/**
 * Why one certificate can't be acted on, in the user's terms.
 *
 * The REASON is the useful part, not just the fact — "already being transferred"
 * is a wait-and-retry, while "reported lost" is a decision the owner has to undo
 * first. A single generic "can't be gifted" collapses those into one
 * unactionable sentence.
 */
const blockReasonFor = (status?: string | null): string => {
  switch (status) {
    case 'transfer_initiated':
    case 'transfer_waiting_confirmation':
    case 'in_transfer':
    case 'out_transfer':
    case 'waiting_accept':
      return 'already being transferred';
    case 'cancel_request':
      return 'has a pending cancellation';
    case 'lost':
      return 'reported lost';
    case 'stolen':
      return 'reported stolen';
    case 'returned':
      return 'been returned';
    default:
      return 'not active';
  }
};

const titleOf = (c: CertificateLike): string =>
  c?.product?.title || c?.name || 'Untitled certificate';

/** "A", "A and B", "A, B and C" */
const joinNames = (names: string[]): string => {
  if (names.length === 1) return names[0];
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
};

export interface BulkActionAvailability {
  enabled: boolean;
  /** One sentence naming what is blocking and why. Empty when enabled. */
  reason: string;
  /**
   * Ids of the selected certificates that block the action — i.e. the ones a
   * "Deselect those N" affordance should drop.
   *
   * EMPTY when the block is not attributable to particular rows (the gift cap
   * below), because there is then no subset to remove: which one to keep is the
   * user's choice, and a control that picked for them would be guessing.
   */
  blockedIds: string[];
}

/**
 * All-or-nothing, deliberately.
 *
 * The tempting alternative — enable it and quietly act on the eligible subset —
 * is worse: someone selects five, four go through, and nothing on screen says the
 * fifth was skipped. Refusing the whole action and naming the blocker keeps the
 * count the user selected and the count that happens identical.
 *
 * NAMES, not counts. "1 of 2 can't be gifted" is true and useless: with a long
 * list the reader still has to work out WHICH one, and the fix ("deselect it")
 * can't be carried out without that. Up to two are named outright; beyond that
 * the names stop helping and the message groups by REASON instead, since a
 * reason is what tells you whether the block is temporary or a decision to undo.
 */
const availabilityForEligible = (
  selected: CertificateLike[],
  emptyReason: string,
): BulkActionAvailability => {
  if (!selected.length) {
    return { enabled: false, reason: emptyReason, blockedIds: [] };
  }

  const blocked = selected.filter(c => !isIdleOwned(c));
  if (!blocked.length) return { enabled: true, reason: '', blockedIds: [] };

  const blockedIds = blocked.map(c => c.id).filter(Boolean) as string[];

  // Few enough to name: the name plus its reason is the most direct route to the fix.
  if (blocked.length <= 2) {
    const byReason = new Map<string, string[]>();
    blocked.forEach(c => {
      const r = blockReasonFor(c.status);
      byReason.set(r, [...(byReason.get(r) ?? []), titleOf(c)]);
    });
    const parts = Array.from(byReason.entries()).map(([reason, names]) => {
      const verb = names.length === 1 ? 'is' : 'are';
      // "reported lost" / "already being transferred" read as "is <reason>";
      // "been returned" needs "has" instead.
      const linking = reason.startsWith('been') ? (names.length === 1 ? 'has' : 'have') : verb;
      return `${joinNames(names)} ${linking} ${reason}`;
    });
    return { enabled: false, reason: `${parts.join(', and ')}.`, blockedIds };
  }

  // Too many to name — group by reason so the message stays one readable line.
  const counts = new Map<string, number>();
  blocked.forEach(c => {
    const r = blockReasonFor(c.status);
    counts.set(r, (counts.get(r) ?? 0) + 1);
  });
  const parts = Array.from(counts.entries()).map(([reason, n]) => {
    // Agree with the COUNT, not the group. Grouping by reason means a group of
    // one is common ("1 is reported lost"), so a hardcoded plural produced
    // "1 are reported lost" in the most likely case rather than an edge one.
    const linking = reason.startsWith('been') ? (n === 1 ? 'has' : 'have') : n === 1 ? 'is' : 'are';
    return `${n} ${linking} ${reason}`;
  });
  return { enabled: false, reason: `${parts.join(', ')}.`, blockedIds };
};

/**
 * GIFT — eligible states, and then a hard cap of ONE.
 *
 * ⚠️ The cap is not a missing batch endpoint. It is the flow.
 *
 * A gift is only sent once the sender has physically scanned THAT item's NFC tag
 * (`GiftCertificate` -> `ScanTag` -> `onScanSuccess` -> POST
 * `/transfer/customer/init/:certificateId`). The scan is the possession proof the
 * whole transfer rests on, so it cannot be hoisted out of the loop: five
 * certificates means five tags in the user's hands and five scans, in a sheet
 * that holds one item, one recipient and one countdown. No amount of client-side
 * sequencing produces that flow; it is a flow nobody has designed. So Gift is
 * offered for a single selection — which is real, complete and already
 * shipping — and refused above it, in as many words.
 *
 * The cap is checked BEFORE eligibility, and that order is deliberate: with three
 * selected, "gift one at a time" is the complete and only relevant instruction,
 * and leading with "the Loafers are reported lost" would send the user to fix
 * something that still leaves them blocked.
 */
export const getGiftAvailability = (selected: CertificateLike[]): BulkActionAvailability => {
  if (selected.length > 1) {
    return {
      enabled: false,
      reason: 'Gifting is one at a time. Select a single certificate.',
      blockedIds: [],
    };
  }
  return availabilityForEligible(selected, 'Select a certificate to gift.');
};

/**
 * REPORT LOST OR STOLEN — the whole selection, no cap.
 *
 * Unlike Gift this needs no tag scan (you are reporting that you no longer HAVE
 * the item, so demanding you scan it would be self-contradicting) and no
 * recipient. It is one form — a status and two notification channels — applied to
 * N records, which is what a bulk action is supposed to be.
 *
 * Stated as its own function rather than an alias even though it currently shares
 * `isIdleOwned` with Gift, because the two answer different questions and will
 * diverge the moment the eligible set moves: Gift needs an item you still hold,
 * Report needs one not ALREADY flagged.
 */
export const getReportMissingAvailability = (
  selected: CertificateLike[],
): BulkActionAvailability =>
  availabilityForEligible(selected, 'Select a certificate to report.');
